import { Alert, Box, Paper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { practiceUsersApi } from "apis";
import {
  PracticeUserFilters,
  PracticeUserSearch,
  PracticeUserTable,
  PracticeUsersLoadingSkeleton,
  PracticeUser,
} from "components/features/practiceManagement";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import {
  ListPracticeUsersRequest,
  ListPracticeUsersRoleEnum,
  ListPracticeUsersSortByEnum,
  ListPracticeUsersSortOrderEnum,
  ListPracticeUsersStatusEnum,
  PracticeUsersResponseDto,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PracticeUserFiltersState {
  search: string;
  role: string;
  status: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  page: number;
  pageSize: number;
}

const DEFAULT_SORT_BY = ListPracticeUsersSortByEnum.AddedDate;
const DEFAULT_SORT_ORDER = ListPracticeUsersSortOrderEnum.Desc;
const DEFAULT_PAGE_SIZE = 10;

const PracticeManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<PracticeUserFiltersState>(() => ({
    search: searchParams.get("search") ?? "",
    role: searchParams.get("role") ?? "",
    status: searchParams.get("status") ?? "",
    sortBy: searchParams.get("sortBy") ?? DEFAULT_SORT_BY,
    sortOrder:
      (searchParams.get("sortOrder") as "asc" | "desc") ?? DEFAULT_SORT_ORDER,
    page: Number(searchParams.get("page") ?? "1"),
    pageSize: Number(searchParams.get("pageSize") ?? DEFAULT_PAGE_SIZE),
  }));

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const newParams = new URLSearchParams();

    if (filters.search) newParams.set("search", filters.search);
    if (filters.role) newParams.set("role", filters.role);
    if (filters.status) newParams.set("status", filters.status);
    if (filters.sortBy !== DEFAULT_SORT_BY)
      newParams.set("sortBy", filters.sortBy);
    if (filters.sortOrder !== DEFAULT_SORT_ORDER)
      newParams.set("sortOrder", filters.sortOrder);
    if (filters.page !== 1) newParams.set("page", String(filters.page));
    if (filters.pageSize !== DEFAULT_PAGE_SIZE)
      newParams.set("pageSize", String(filters.pageSize));

    setSearchParams(newParams, { replace: true });
  }, [filters, setSearchParams]);

  const queryParams = useMemo(
    (): ListPracticeUsersRequest => ({
      search: debouncedSearch || undefined,
      role: (filters.role as ListPracticeUsersRoleEnum) || undefined,
      status: (filters.status as ListPracticeUsersStatusEnum) || undefined,
      sortBy: (filters.sortBy as ListPracticeUsersSortByEnum) || undefined,
      sortOrder:
        (filters.sortOrder as ListPracticeUsersSortOrderEnum) || undefined,
      page: filters.page,
      pageSize: filters.pageSize,
    }),
    [
      debouncedSearch,
      filters.page,
      filters.pageSize,
      filters.role,
      filters.sortBy,
      filters.sortOrder,
      filters.status,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    PracticeUsersResponseDto,
    Error
  >({
    queryKey: ["practiceUsers", queryParams],
    queryFn: () =>
      practiceUsersApi.listPracticeUsersForAdmin({
        ...queryParams,
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
  });

  const practiceUsers = useMemo<PracticeUser[]>(() => {
    if (!data?.practiceUsers) return [];

    return data.practiceUsers.map((user) => ({
      id: user.id,
      accountId: user.accountId,
      email: user.email,
      name: user.name,
      phoneNumber: user.phoneNumber,
      phoneNumberCountryCode: user.phoneNumberCountryCode,
      phoneNumberCountryIso2: user.phoneNumberCountryIso2,
      role: user.role,
      npi: user.npi,
      branches: user.branches,
      specialties: user.specialties,
      status: user.status,
      addedDate: user.addedDate,
    }));
  }, [data?.practiceUsers]);

  const totalCount = data?.pagination?.total ?? 0;

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleRoleChange = useCallback((roleValue: string) => {
    setFilters((prev) => ({ ...prev, role: roleValue, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((statusValue: string) => {
    setFilters((prev) => ({ ...prev, status: statusValue, page: 1 }));
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: "asc" | "desc") => {
      setFilters((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleUserClick = useCallback(
    (userId: string) => {
      navigate(EPath.PRACTICE_USER_DETAIL.replace(":practiceUserId", userId));
    },
    [navigate],
  );

  return (
    <DashboardLayout title="Practice Management">
      <Box sx={{ p: 2 }}>
        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Box
                component="button"
                onClick={() => refetch()}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.primary.main,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Retry
              </Box>
            }
          >
            {error instanceof Error
              ? error.message
              : "Failed to load practice users"}
          </Alert>
        ) : null}

        <Paper sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <PracticeUserSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: { xs: "100%", md: "auto" },
                ml: { md: "auto" },
                alignSelf: { xs: "flex-end", md: "auto" },
              }}
            >
              <PracticeUserFilters
                role={filters.role}
                status={filters.status}
                onRoleChange={handleRoleChange}
                onStatusChange={handleStatusChange}
              />
            </Box>
          </Box>

          {isLoading && practiceUsers.length === 0 ? (
            <PracticeUsersLoadingSkeleton />
          ) : (
            <PracticeUserTable
              practiceUsers={practiceUsers}
              loading={isFetching}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSortChange={handleSortChange}
              onUserClick={handleUserClick}
              pagination={{
                page: filters.page,
                pageSize: filters.pageSize,
                total: totalCount,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }}
            />
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default PracticeManagementPage;
